import { useState } from "react";
import { Plus, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtL } from "@/lib/api";
import { PageHeader, Badge, TeamBadge, useConfirm } from "@/components/opl/Common";
import { useAuth } from "@/context/AuthContext";
import { useList, FormDialog, DataTable, GoldBtn, IconBtn } from "@/components/opl/Crud";

export default function Teams() {
  const [rows, load] = useList("/teams");
  const [clubs] = useList("/clubs");
  const { can } = useAuth();
  const [users] = useList(can("manage_users") ? "/users" : "/teams");
  const [edit, setEdit] = useState(null);
  const [dialog, confirm] = useConfirm();
  const umap = Object.fromEntries((users || []).map((u) => [u.id, u]));
  const cmap = Object.fromEntries((clubs || []).map((c) => [c.id, c.name]));
  const fields = [
    { name: "name", label: "Official team name", required: true }, { name: "short_name", label: "Short code", hint: "2–4 letters" },
    { name: "owner_name", label: "Owner name", required: true },
    { name: "club_id", label: "Club / branch", type: "select", options: (clubs || []).map((c) => ({ value: c.id, label: c.name })) },
    { name: "captain", label: "Captain" }, { name: "manager", label: "Manager" },
    { name: "initial_purse", label: "Initial purse (points)", type: "number", hint: "Defaults to configured team purse" },
    { name: "reason", label: "Reason for purse change", hidden: (v) => !v.id || !v.spent },
    { name: "color", label: "Team colour", type: "color" }, { name: "logo", label: "Team logo", type: "image", folder: "teams" },
    { name: "active", label: "Active", type: "switch" },
  ];
  const save = async (v) => {
    const body = { ...v };
    if (v.id) await api.put(`/teams/${v.id}`, body); else await api.post("/teams", body);
    toast.success("Team saved");
    load();
  };
  return (
    <div>
      {dialog}
      <PageHeader title="Teams" crumbs={["Teams"]}><GoldBtn testid="add-team-btn" onClick={() => setEdit({ active: true, color: "#E5B842" })}><Plus className="h-4 w-4" />Add team</GoldBtn></PageHeader>
      <p className="text-sm text-slate-400 mb-4">Team logins are created under <b>Owners & Users</b> (role: Team Owner, linked to the team).</p>
      <DataTable rows={rows} testid="teams-table" columns={[
        { key: "name", label: "Team", render: (r) => <div className="flex items-center gap-3"><TeamBadge team={r} size={32} /><div><b>{r.name}</b>{r.official_name_pending && <div className="text-[10px] text-orange-300">Official name pending</div>}</div></div> },
        { key: "owner_name", label: "Owner" }, { key: "club", label: "Club", render: (r) => cmap[r.club_id] || "—" },
        { key: "captain", label: "Captain / Manager", render: (r) => <span className="text-slate-400">{r.captain || "—"} / {r.manager || "—"}</span> },
        { key: "login", label: "Login", render: (r) => umap[r.user_id]?.email || (r.user_id ? "Linked" : null) || <span className="text-orange-300 text-xs">Not linked</span> },
        { key: "purse", label: "Purse", render: (r) => <span className="num">{fmtL(r.remaining_purse)} <span className="text-slate-500">/ {fmtL(r.initial_purse)}</span></span> },
        { key: "spent", label: "Spent", render: (r) => <span className="num">{fmtL(r.spent)}</span> },
        { key: "players_count", label: "Players" },
        { key: "active", label: "Status", render: (r) => <Badge className={r.active ? "text-emerald-300 border-emerald-500/40" : "text-slate-400"}>{r.active ? "Active" : "Inactive"}</Badge> },
        { key: "a", label: "", render: (r) => <div className="flex justify-end"><IconBtn testid={`edit-team-${r.code}`} onClick={() => setEdit({ ...r, reason: "" })}><Pencil className="h-4 w-4" /></IconBtn>
          <IconBtn testid={`archive-team-${r.code}`} onClick={() => confirm({ title: `Archive ${r.name}?`, description: "Teams with completed purchases are protected.", destructive: true, confirmLabel: "Archive", onConfirm: async () => { try { await api.delete(`/teams/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } })}><Archive className="h-4 w-4" /></IconBtn></div> },
      ]} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit team" : "Add team"} fields={fields} initial={edit} onSubmit={save} />
    </div>
  );
}
